export type PayzheBtpErrorCode =
  | 'not_installed'
  | 'not_running'
  | 'outdated'
  | 'incompatible_web'
  | 'service_stopped'
  | 'auth_failed'
  | 'printer_not_found'
  | 'connect_failed'
  | 'print_failed'
  | 'queue_failed'
  | 'queue_full'
  | 'unreachable'
  | 'timeout';

export type PayzheBtpRecoveryAction =
  | 'install_apk'
  | 'update_apk'
  | 'update_web'
  | 'wake_app'
  | 'restart_service'
  | 're_handshake'
  | 'open_printer_page'
  | 'use_browser_fallback'
  | 'retry';

export class PayzheBtpError extends Error {
  constructor(
    message: string,
    readonly code: PayzheBtpErrorCode,
    readonly errorKey?: string,
    readonly recovery?: PayzheBtpRecoveryAction,
  ) {
    super(message);
    this.name = 'PayzheBtpError';
  }
}

export function mapNativeErrorToPayzheBtpError(error: unknown): PayzheBtpError {
  if (error instanceof PayzheBtpError) return error;

  const message = error instanceof Error ? error.message : String(error);
  const code = (error as { code?: string })?.code;

  if (message.includes('browserBlocked')) {
    return new PayzheBtpError(message, 'unreachable', 'bluetoothPrinter.payzheBtp.errors.browserBlocked', 'wake_app');
  }
  if (message.includes('connectionRefused')) {
    return new PayzheBtpError(message, 'not_running', 'bluetoothPrinter.payzheBtp.errors.connectionRefused', 'wake_app');
  }
  if (message.includes('originDenied')) {
    return new PayzheBtpError(message, 'unreachable', 'bluetoothPrinter.payzheBtp.errors.originDenied', 'retry');
  }
  if (message.includes('timeout') || message.includes('AbortError')) {
    return new PayzheBtpError(message, 'timeout', 'bluetoothPrinter.payzheBtp.errors.timeout', 'retry');
  }
  if (message.includes('Failed to fetch')) {
    return new PayzheBtpError(
      message,
      'not_running',
      'bluetoothPrinter.payzheBtp.errors.connectionRefused',
      'wake_app',
    );
  }
  if (message.includes('unreachable')) {
    return new PayzheBtpError(message, 'not_running', 'bluetoothPrinter.payzheBtp.errors.connectionRefused', 'wake_app');
  }
  if (code === 'SERVICE_UNAVAILABLE') {
    return new PayzheBtpError(message, 'service_stopped', 'bluetoothPrinter.payzheBtp.errors.serviceStopped', 'wake_app');
  }
  if (code === 'PRINTER_NOT_FOUND') {
    return new PayzheBtpError(message, 'printer_not_found', 'bluetoothPrinter.payzheBtp.errors.printerNotFound', 'open_printer_page');
  }
  if (code === 'PRINTER_DISCONNECTED' || code === 'connect_failed') {
    return new PayzheBtpError(message, 'connect_failed', 'bluetoothPrinter.payzheBtp.errors.connectFailed', 'retry');
  }
  if (code === 'QUEUE_FULL') {
    return new PayzheBtpError(message, 'queue_full', 'bluetoothPrinter.payzheBtp.errors.queueFull', 'retry');
  }
  if (code === 'queue_failed' || message.includes('printFailedQueue')) {
    return new PayzheBtpError(
      message,
      'queue_failed',
      'bluetoothPrinter.payzheBtp.printFailedQueue',
      'open_printer_page',
    );
  }
  if (message.startsWith('bluetoothPrinter.')) {
    return new PayzheBtpError(message, 'unreachable', message, 'retry');
  }

  return new PayzheBtpError(message, 'unreachable', 'bluetoothPrinter.payzheBtp.errors.unreachable', 'retry');
}
